import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Calendar, 
  MapPin, 
  Clock, 
  Users, 
  ArrowRight,
  Search,
  BookOpen,
  Globe,
  Heart,
  Share2
} from 'lucide-react';
const BlogPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const workshops = [
    {
      id: 1,
      title: "Resin Art Workshop in Udaipur",
      location: "Udaipur, Rajasthan",
      type: "In-Person",
      date: "2024-08-15",
      duration: "3 days",
      price: "₹8,500",
      level: "Beginner",
      instructor: "Master Craftsman Raj Kumar",
      image: "https://images.unsplash.com/photo-1541961017774-22349e4a1262?w=600&h=400&fit=crop",
      description: "Learn the fundamentals of resin art in the beautiful city of Udaipur. Perfect for beginners looking to start their resin journey.",
      features: ["All materials included", "Certificate of completion", "Take home 3 projects", "Lifetime support group"],
      spots: 12,
      booked: 8
    },
    {
      id: 2,
      title: "Online Resin Art Workshop",
      location: "Online",
      type: "Virtual",
      date: "2024-08-20",
      duration: "5 sessions",
      price: "₹3,500",
      level: "All Levels",
      instructor: "Priya Sharma",
      image: "https://images.unsplash.com/photo-1588196749597-9ff075ee6b5b?w=600&h=400&fit=crop",
      description: "Join from anywhere in the world! Interactive online sessions with live demonstrations and personalized feedback.",
      features: ["Live interactive sessions", "Recorded sessions access", "Materials kit shipped", "1-on-1 mentoring"],
      spots: 25,
      booked: 18
    },
    {
      id: 3,
      title: "Best Resin Art Workshop",
      location: "Mumbai, Maharashtra",
      type: "In-Person",
      date: "2024-08-25",
      duration: "2 days",
      price: "₹12,000",
      level: "Advanced",
      instructor: "Arjun Malhotra",
      image: "https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=600&h=400&fit=crop",
      description: "Advanced techniques workshop covering professional-grade methods and business insights for serious artists.",
      features: ["Advanced techniques", "Business guidance", "Portfolio review", "Industry connections"],
      spots: 8,
      booked: 6
    },
    {
      id: 4,
      title: "Resin Art Classes in Udaipur",
      location: "Udaipur, Rajasthan",
      type: "In-Person",
      date: "2024-09-01",
      duration: "1 month",
      price: "₹15,000",
      level: "Comprehensive",
      instructor: "Meera Joshi",
      image: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=600&h=400&fit=crop",
      description: "Comprehensive month-long course covering all aspects of resin art from basics to advanced techniques.",
      features: ["30+ hours of training", "Personal studio access", "Exhibition opportunity", "Material kit worth ₹5000"],
      spots: 15,
      booked: 12
    },
    {
      id: 5,
      title: "Resin Artwork Shop in Ahmedabad",
      location: "Ahmedabad, Gujarat",
      type: "Workshop & Shop",
      date: "2024-09-10",
      duration: "2 days",
      price: "₹6,500",
      level: "Intermediate",
      instructor: "Kiran Patel",
      image: "https://images.unsplash.com/photo-1452860606245-08befc0ff44b?w=600&h=400&fit=crop",
      description: "Learn while you create! Workshop combined with guided shopping for premium resin art supplies.",
      features: ["Workshop + Shopping", "Discount on supplies", "Expert guidance", "Network with local artists"],
      spots: 20,
      booked: 14
    },
    {
      id: 6,
      title: "Learn Resin Art - Complete Guide",
      location: "Online",
      type: "Self-Paced",
      date: "Available Now",
      duration: "Lifetime Access",
      price: "₹4,999",
      level: "All Levels",
      instructor: "ResinCraft Studio Team",
      image: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=600&h=400&fit=crop",
      description: "Complete self-paced course with over 50+ video lessons, downloadable resources, and community support.",
      features: ["50+ video lessons", "Downloadable resources", "Community access", "Monthly Q&A sessions"],
      spots: "Unlimited",
      booked: 247
    }
  ];

  const blogPosts = [
    {
      id: 1,
      title: "Resin Art in India: A Growing Movement",
      excerpt: "Discover how resin art is taking India by storm with artists from Mumbai to Chennai creating stunning masterpieces.",
      author: "Priya Sharma",
      date: "2024-07-15",
      readTime: "8 min read",
      category: "Industry",
      image: "https://images.unsplash.com/photo-1541961017774-22349e4a1262?w=600&h=400&fit=crop",
      tags: ["India", "Art Movement", "Culture"],
      likes: 245,
      comments: 18
    },
    {
      id: 2,
      title: "Best Resin Art Workshop: What to Expect",
      excerpt: "A comprehensive guide to choosing the right resin art workshop and what makes a workshop truly exceptional.",
      author: "Arjun Malhotra",
      date: "2024-07-10",
      readTime: "6 min read",
      category: "Education",
      image: "https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=600&h=400&fit=crop",
      tags: ["Workshop", "Education", "Tips"],
      likes: 189,
      comments: 24
    },
    {
      id: 3,
      title: "Creating Stunning Resin Clocks: A Step-by-Step Guide",
      excerpt: "Learn how to create beautiful, functional resin clocks that serve as both timepieces and art pieces.",
      author: "Meera Joshi",
      date: "2024-07-05",
      readTime: "12 min read",
      category: "Tutorial",
      image: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=600&h=400&fit=crop",
      tags: ["Clock", "Tutorial", "DIY"],
      likes: 156,
      comments: 31
    },
    {
      id: 4,
      title: "Handmade Resin Trays: Perfect for Beginners",
      excerpt: "Start your resin journey with these beautiful and functional trays that make perfect gifts or home decor.",
      author: "Kiran Patel",
      date: "2024-06-28",
      readTime: "5 min read",
      category: "Beginner",
      image: "https://images.unsplash.com/photo-1452860606245-08befc0ff44b?w=600&h=400&fit=crop",
      tags: ["Tray", "Beginner", "Home Decor"],
      likes: 203,
      comments: 15
    },
    {
      id: 5,
      title: "Wooden Resin Coasters: Merging Nature and Art",
      excerpt: "Combine the beauty of wood with the versatility of resin to create stunning coasters that protect and beautify.",
      author: "Raj Kumar",
      date: "2024-06-20",
      readTime: "7 min read",
      category: "Project",
      image: "https://images.unsplash.com/photo-1588196749597-9ff075ee6b5b?w=600&h=400&fit=crop",
      tags: ["Coaster", "Wood", "Nature"],
      likes: 178,
      comments: 22
    },
    {
      id: 6,
      title: "Advanced Resin Techniques: From Amateur to Professional",
      excerpt: "Take your resin art to the next level with these advanced techniques used by professional artists worldwide.",
      author: "Master Craftsman Team",
      date: "2024-06-15",
      readTime: "15 min read",
      category: "Advanced",
      image: "https://images.unsplash.com/photo-1541961017774-22349e4a1262?w=600&h=400&fit=crop",
      tags: ["Advanced", "Professional", "Techniques"],
      likes: 298,
      comments: 42
    }
  ];

  const categories = [
    { id: 'all', name: 'All Posts', count: blogPosts.length },
    { id: 'tutorial', name: 'Tutorials', count: 2 },
    { id: 'workshop', name: 'Workshops', count: 1 },
    { id: 'industry', name: 'Industry', count: 1 },
    { id: 'beginner', name: 'Beginner', count: 1 },
    { id: 'advanced', name: 'Advanced', count: 1 }
  ];

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === 'all' || post.category.toLowerCase() === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <div className="min-h-screen bg-dark-950 pt-20">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-dark-900 to-dark-950 relative overflow-hidden">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold font-serif text-white mb-6">
              Resin Art <span className="bg-gradient-to-r from-accent-400 to-accent-600 bg-clip-text text-transparent">Blog</span>
            </h1>
            <p className="text-xl text-dark-200 max-w-3xl mx-auto leading-relaxed">
              Discover the world of resin art through our comprehensive guides, workshops, and expert insights. 
              Learn from the best and join our growing community of artists.
            </p>
          </motion.div>
        </div>
        
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-accent-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-accent-600/10 rounded-full blur-3xl"></div>
      </section>

      {/* Workshops Section */}
      <section className="py-16 bg-dark-800">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-white mb-6">
              Upcoming <span className="bg-gradient-to-r from-accent-400 to-accent-600 bg-clip-text text-transparent">Workshops</span>
            </h2>
            <p className="text-dark-300 max-w-2xl mx-auto">
              Join our expert-led workshops and master the art of resin crafting. From beginner to advanced levels.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {workshops.map((workshop, index) => (
              <motion.div
                key={workshop.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-dark-700/50 backdrop-blur-md rounded-2xl overflow-hidden border border-dark-600 hover:border-accent-500/30 transition-all duration-300 hover:scale-105"
              >
                <div className="relative">
                  <img
                    src={workshop.image}
                    alt={workshop.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute top-4 left-4 flex space-x-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      workshop.type === 'Online' ? 'bg-blue-500 text-white' : 
                      workshop.type === 'Virtual' ? 'bg-purple-500 text-white' :
                      'bg-green-500 text-white'
                    }`}>
                      {workshop.type}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      workshop.level === 'Beginner' ? 'bg-green-500/80 text-white' :
                      workshop.level === 'Intermediate' ? 'bg-yellow-500/80 text-white' :
                      workshop.level === 'Advanced' ? 'bg-red-500/80 text-white' :
                      'bg-purple-500/80 text-white'
                    }`}>
                      {workshop.level}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4">
                    <div className="bg-black/50 backdrop-blur-md px-3 py-1 rounded-full">
                      <span className="text-white font-bold text-lg">{workshop.price}</span>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-semibold text-white mb-2 line-clamp-2">
                    {workshop.title}
                  </h3>
                  <p className="text-dark-300 text-sm mb-4 line-clamp-2">
                    {workshop.description}
                  </p>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-dark-300">
                      <MapPin className="h-4 w-4 mr-2 text-accent-400" />
                      {workshop.location}
                    </div>
                    <div className="flex items-center text-sm text-dark-300">
                      <Calendar className="h-4 w-4 mr-2 text-accent-400" />
                      {workshop.date}
                    </div>
                    <div className="flex items-center text-sm text-dark-300">
                      <Clock className="h-4 w-4 mr-2 text-accent-400" />
                      {workshop.duration}
                    </div>
                    <div className="flex items-center text-sm text-dark-300">
                      <Users className="h-4 w-4 mr-2 text-accent-400" />
                      {workshop.spots === 'Unlimited' ? 'Unlimited' : `${workshop.booked}/${workshop.spots} spots`}
                    </div>
                  </div>

                  <div className="mb-4">
                    <h4 className="text-white font-medium mb-2 text-sm">What's Included:</h4>
                    <ul className="text-xs text-dark-300 space-y-1">
                      {workshop.features.slice(0, 2).map((feature, idx) => (
                        <li key={idx} className="flex items-center">
                          <div className="w-1 h-1 bg-accent-400 rounded-full mr-2"></div>
                          {feature}
                        </li>
                      ))}
                      {workshop.features.length > 2 && (
                        <li className="text-accent-400">+{workshop.features.length - 2} more</li>
                      )}
                    </ul>
                  </div>

                  <div className="flex space-x-2">
                    <button className="flex-1 bg-gradient-to-r from-accent-500 to-accent-600 text-white py-2 px-4 rounded-lg font-medium hover:from-accent-600 hover:to-accent-700 transition-all duration-300 text-sm">
                      Book Now
                    </button>
                    <button className="border border-accent-500 text-accent-400 py-2 px-4 rounded-lg font-medium hover:bg-accent-500/10 transition-all duration-300 text-sm">
                      Details
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            ref={ref}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">
                Latest <span className="bg-gradient-to-r from-accent-400 to-accent-600 bg-clip-text text-transparent">Articles</span>
              </h2>
              <p className="text-dark-300 max-w-2xl mx-auto">
                Stay updated with the latest trends, techniques, and insights from the world of resin art.
              </p>
            </motion.div>

            {/* Search and Filter */}
            <motion.div variants={itemVariants} className="mb-12">
              <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-6 lg:space-y-0">
                {/* Search */}
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-dark-400" />
                  <input
                    type="text"
                    placeholder="Search articles..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-dark-800 border border-dark-600 rounded-lg pl-10 pr-4 py-3 text-white placeholder-dark-400 focus:border-accent-500 focus:outline-none"
                  />
                </div>

                {/* Categories */}
                <div className="flex flex-wrap gap-2">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                        selectedCategory === category.id
                          ? 'bg-gradient-to-r from-accent-500 to-accent-600 text-white'
                          : 'bg-dark-800 text-dark-300 hover:bg-dark-700 hover:text-white'
                      }`}
                    >
                      {category.name} ({category.count})
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Blog Posts Grid */}
            <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post, index) => (
                <motion.article
                  key={post.id}
                  variants={itemVariants}
                  className="bg-dark-800/50 backdrop-blur-md rounded-2xl overflow-hidden border border-dark-700 hover:border-accent-500/30 transition-all duration-300 hover:scale-105 cursor-pointer"
                >
                  <div className="relative">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-48 object-cover"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-accent-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                        {post.category}
                      </span>
                    </div>
                    <div className="absolute top-4 right-4 flex space-x-2">
                      <button className="bg-black/50 backdrop-blur-md p-2 rounded-full hover:bg-black/70 transition-colors duration-300">
                        <Heart className="h-4 w-4 text-white" />
                      </button>
                      <button className="bg-black/50 backdrop-blur-md p-2 rounded-full hover:bg-black/70 transition-colors duration-300">
                        <Share2 className="h-4 w-4 text-white" />
                      </button>
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="flex items-center text-sm text-dark-400 mb-3">
                      <span>{post.author}</span>
                      <span className="mx-2">•</span>
                      <span>{post.date}</span>
                      <span className="mx-2">•</span>
                      <span>{post.readTime}</span>
                    </div>

                    <h3 className="text-xl font-semibold text-white mb-3 line-clamp-2">
                      {post.title}
                    </h3>

                    <p className="text-dark-300 text-sm mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>

                    <div className="flex flex-wrap gap-2 mb-4">
                      {post.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="bg-dark-700 text-dark-300 px-2 py-1 rounded text-xs"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 text-sm text-dark-400">
                        <div className="flex items-center space-x-1">
                          <Heart className="h-4 w-4" />
                          <span>{post.likes}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <BookOpen className="h-4 w-4" />
                          <span>{post.comments}</span>
                        </div>
                      </div>
                      
                      <button className="text-accent-400 hover:text-accent-300 transition-colors duration-300 flex items-center space-x-1">
                        <span className="text-sm font-medium">Read More</span>
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </motion.article>
              ))}
            </motion.div>

            {/* No Results */}
            {filteredPosts.length === 0 && (
              <motion.div
                variants={itemVariants}
                className="text-center py-16"
              >
                <BookOpen className="h-16 w-16 text-dark-600 mx-auto mb-4" />
                <h3 className="text-2xl font-semibold text-white mb-4">
                  No articles found
                </h3>
                <p className="text-dark-300 mb-6">
                  Try adjusting your search terms or browse all articles.
                </p>
                <button
                  onClick={() => {
                    setSelectedCategory('all');
                    setSearchTerm('');
                  }}
                  className="bg-gradient-to-r from-accent-500 to-accent-600 text-white py-3 px-6 rounded-lg font-medium hover:from-accent-600 hover:to-accent-700 transition-all duration-300"
                >
                  View All Articles
                </button>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 bg-dark-800">
        <div className="container mx-auto px-4 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-br from-accent-500/20 to-accent-600/20 rounded-3xl p-12 text-center border border-accent-500/30"
          >
            <Globe className="h-16 w-16 text-accent-400 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-6">
              Stay Connected
            </h2>
            <p className="text-xl text-dark-200 mb-8 max-w-2xl mx-auto">
              Subscribe to our newsletter for the latest workshops, tutorials, and resin art insights delivered to your inbox.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 bg-dark-800 border border-dark-600 rounded-lg px-4 py-3 text-white placeholder-dark-400 focus:border-accent-500 focus:outline-none"
              />
              <button className="bg-gradient-to-r from-accent-500 to-accent-600 text-white py-3 px-6 rounded-lg font-medium hover:from-accent-600 hover:to-accent-700 transition-all duration-300">
                Subscribe
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;